import {
	createRouter, createWebHistory, LocationQuery, NavigationGuardWithThis, RouteLocationNormalized,
} from 'vue-router';
import { authGuard as auth0Guard } from '@auth0/auth0-vue';
import DashboardPage from 'views/DashboardPage.vue';
import ShowOrderPage from 'views/ShowOrderPage.vue';
import UserPropertiesPage from 'views/UserPropertiesPage.vue';
import { rememberReturnTo } from '../auth/returnTo';
import { modulePageRoutes } from '../config/moduleRegistry';
import LayoutBackend from '../components/layouts/variations/Backend.vue';

declare const DEV_BYPASS_AUTH: string | undefined;

const isDevBypass = typeof DEV_BYPASS_AUTH !== 'undefined' && DEV_BYPASS_AUTH === 'true';

// True when the location carries the Auth0 redirect-callback params. During the
// callback the URL temporarily lands on /dashboard (see the root redirect
// below), so we must NOT treat that as the user's intended destination.
function hasAuthCallback(to: { query: LocationQuery }): boolean {
	return Boolean((to.query.code && to.query.state) || to.query.error);
}

// Wrap the Auth0 guard so we remember where the user was heading before being
// redirected to login. After authenticating the SDK returns to the bare origin
// (redirect_uri), so without this the original deep link would be lost and the
// user would always land on /dashboard. `main.ts` restores the remembered path
// once the Auth0 callback has been processed.
const authGuard: NavigationGuardWithThis<undefined> = isDevBypass
	? () => true
	: (to) => {
		if (!hasAuthCallback(to) && to.path !== '/') {
			rememberReturnTo(to.fullPath);
		}
		return (auth0Guard as (t: RouteLocationNormalized) => Promise<boolean>)(to);
	};

const router = createRouter({
	history: createWebHistory('/'),
	routes: [
		{
			path: '/',
			component: LayoutBackend,
			beforeEnter: authGuard,
			children: [
				{
					path: '',
					name: 'home',
					// Normally the index route redirects to the dashboard.
					// But on an Auth0 redirect callback the URL carries
					// `?code`/`state` (or `?error`) params that the SDK must
					// read to complete login. A plain string redirect to
					// `/dashboard` would drop those params before the SDK
					// consumes them, leaving the user unauthenticated and
					// causing an infinite login redirect loop in production.
					// Preserve the query so the callback params survive; the
					// SDK then cleans the URL and navigates to the post-login
					// target itself.
					redirect: (to) => (hasAuthCallback(to)
						? { path: '/dashboard', query: to.query }
						: { path: '/dashboard' }),
				},
				{
					path: 'dashboard',
					name: 'Dashboard',
					component: DashboardPage,
					beforeEnter: authGuard,
				},
				{
					path: '/showOrder/:orderid',
					name: 'showOrder',
					component: ShowOrderPage,
					beforeEnter: authGuard,
				},
				{
					path: '/appuser/:id',
					name: 'UserProperties',
					component: UserPropertiesPage,
					beforeEnter: authGuard,
				},
				...modulePageRoutes.map((route) => ({
					...route,
					beforeEnter: authGuard,
				})),
			],
		},
	],
});

export default router;